/**
 * Admin Pricing Rules Page
 * 
 * Manage floor price baselines by vertical, lead type, and state.
 * Organized by lead type cards with nested state overrides.
 */

import { useState, useMemo } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { usePricingRules, PricingRule } from "@/contexts/PricingRulesContext";
import { PricingRuleCard } from "@/components/admin/PricingRuleCard";
import { Card, CardContent } from "@/components/ui/card";
import { Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type VerticalFilter = "all" | "life_insurance" | "mortgage";

interface LeadTypeGroup {
  leadTypeId: string;
  leadTypeName: string;
  vertical: "life_insurance" | "mortgage";
  baseRule: PricingRule | null;
  stateOverrides: PricingRule[];
}

export default function AdminPricing() {
  const { state, actions } = usePricingRules();
  const [verticalFilter, _setVerticalFilter] = useState<VerticalFilter>("all");

  // Group rules by lead type
  const leadTypeGroups = useMemo(() => {
    const groups = new Map<string, LeadTypeGroup>();

    state.rules.forEach((rule) => {
      if (!groups.has(rule.leadTypeId)) {
        groups.set(rule.leadTypeId, {
          leadTypeId: rule.leadTypeId,
          leadTypeName: rule.leadTypeName,
          vertical: rule.vertical,
          baseRule: null,
          stateOverrides: [],
        });
      }

      const group = groups.get(rule.leadTypeId)!;
      if (rule.state === "ALL") {
        group.baseRule = rule;
      } else {
        group.stateOverrides.push(rule);
      }
    });

    // Sort overrides by state
    groups.forEach((group) => {
      group.stateOverrides.sort((a, b) => a.state.localeCompare(b.state));
    });

    return Array.from(groups.values());
  }, [state.rules]);

  // Filter by vertical
  const filteredGroups = useMemo(() => {
    if (verticalFilter === "all") return leadTypeGroups;
    return leadTypeGroups.filter((g) => g.vertical === verticalFilter);
  }, [leadTypeGroups, verticalFilter]);

  // Separate by vertical for tabs view
  const lifeInsuranceGroups = filteredGroups.filter((g) => g.vertical === "life_insurance");
  const mortgageGroups = filteredGroups.filter((g) => g.vertical === "mortgage");

  const handleUpdatePrice = (id: string, newPrice: number) => {
    actions.updatePricingRule(id, { basePrice: newPrice });
  };

  const handleToggleActive = (id: string) => {
    actions.togglePricingRuleActive(id);
  };

  const handleAddOverride = (rule: Omit<PricingRule, "id">) => {
    actions.addPricingRule(rule);
  };

  const handleDeleteRule = (id: string) => {
    actions.deletePricingRule(id);
  };

  // Stats
  const activeRules = state.rules.filter((r) => r.active).length;
  const stateOverrideCount = state.rules.filter((r) => r.state !== "ALL").length;
  const avgFloorPrice = state.rules.filter((r) => r.active).reduce((sum, r) => sum + r.basePrice, 0) / 
    Math.max(state.rules.filter((r) => r.active).length, 1);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Floor Pricing</h1>
          <p className="text-muted-foreground">
            Define floor baselines by vertical, lead type, and state (observability only)
          </p>
        </div>

        {/* Info Alert */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Set a <strong>default floor</strong> for each lead type, then add <strong>state overrides</strong> for 
            specific markets. Floors are tracked for margin alerts and reporting — they do not block delivery.
          </AlertDescription>
        </Alert>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{activeRules}</div>
              <p className="text-sm text-muted-foreground">Active Rules</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stateOverrideCount}</div>
              <p className="text-sm text-muted-foreground">State Overrides</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold font-mono">${avgFloorPrice.toFixed(2)}</div>
              <p className="text-sm text-muted-foreground">Avg. Floor Price</p>
            </CardContent>
          </Card>
        </div>

        {/* Vertical Tabs */}
        <Tabs defaultValue="all" className="space-y-4">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="all">All Verticals</TabsTrigger>
              <TabsTrigger value="life_insurance">Life Insurance</TabsTrigger>
              <TabsTrigger value="mortgage">Mortgage</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="all" className="space-y-4">
            {leadTypeGroups.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No pricing rules configured.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {leadTypeGroups.map((group) => (
                  <PricingRuleCard
                    key={group.leadTypeId}
                    {...group}
                    onUpdatePrice={handleUpdatePrice}
                    onToggleActive={handleToggleActive}
                    onAddOverride={handleAddOverride}
                    onDeleteRule={handleDeleteRule}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="life_insurance" className="space-y-4">
            {lifeInsuranceGroups.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No Life Insurance pricing rules.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {lifeInsuranceGroups.map((group) => (
                  <PricingRuleCard
                    key={group.leadTypeId}
                    {...group}
                    onUpdatePrice={handleUpdatePrice}
                    onToggleActive={handleToggleActive}
                    onAddOverride={handleAddOverride}
                    onDeleteRule={handleDeleteRule}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="mortgage" className="space-y-4">
            {mortgageGroups.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No Mortgage pricing rules.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {mortgageGroups.map((group) => (
                  <PricingRuleCard
                    key={group.leadTypeId}
                    {...group}
                    onUpdatePrice={handleUpdatePrice}
                    onToggleActive={handleToggleActive}
                    onAddOverride={handleAddOverride}
                    onDeleteRule={handleDeleteRule}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
